import { Head, Link } from "@inertiajs/react"
import { formatDistanceToNow } from "date-fns"
import { es } from "date-fns/locale"
import { ArrowLeft, CheckCircle, Eye } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { feedPath } from "@/routes"

interface Viewer {
  id: number
  name: string
  avatar_url: string | null
  verified: boolean
  viewed_at: string
}

interface Story {
  id: number
  content: string
}

interface ViewersPageProps {
  story: Story
  viewers: Viewer[]
  total: number
}

export default function ViewersPage({ viewers, total }: ViewersPageProps) {
  return (
    <AppLayout>
      <Head title={`Visualizaciones - Historia`} />

      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-2xl px-4 py-6">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link href={feedPath()}>
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Volver
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Visualizaciones
                </h1>
                <p className="text-sm text-gray-600">
                  {total}{" "}
                  {total === 1 ? "persona ha visto" : "personas han visto"} tu
                  historia
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-blue-600">
              <Eye className="h-5 w-5" />
              <span className="font-medium">{total}</span>
            </div>
          </div>

          {/* Viewers List */}
          <div className="bg-card divide-y rounded-lg border">
            {viewers.length === 0 ? (
              <div className="p-8 text-center">
                <Eye className="mx-auto mb-3 h-12 w-12 text-gray-400" />
                <p className="text-gray-600">
                  Nadie ha visto tu historia todavía
                </p>
              </div>
            ) : (
              viewers.map((viewer) => (
                <div
                  key={viewer.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={viewer.avatar_url ?? undefined} />
                      <AvatarFallback>
                        {viewer.name
                          .split(" ")
                          .map((n) => n[0] ?? "")
                          .join("")
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {viewer.name}
                        </p>
                        {viewer.verified && (
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        Visto hace{" "}
                        {formatDistanceToNow(new Date(viewer.viewed_at), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Visualizado
                  </Badge>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {viewers.length > 0 && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Mostrando {viewers.length} de {total} visualizaciones
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
